import {
  ReportDailyResultImg,
  RiskLabel,
} from "@/pages/common/report/WebReport2/commons";
import { SLEEP_JUDGE_TITLE_CONFIG } from "@/pages/common/report/WebReport2/SleepJudge/commons";
import { formatDisplayTime } from "@/utils/date";
import { NavigateNext } from "@mui/icons-material";
import {
  Breadcrumbs,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { format, parse } from "date-fns";
import Highcharts, { SeriesLegendItemClickEventObject } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useRef } from "react";
import ReportNavigate from "./commons/ReportNavigate";
import { ScrollableContainer } from "./commons/ScrollableContainer";
import SubtitleTypography from "./commons/SubtitleTypography";
import { Quantity2Response, UserType } from "./types";

export default function Quantity2({
  uuid,
  userType,
  data,
}: {
  uuid: string | undefined;
  userType: UserType;
  data: Quantity2Response;
}) {
  return (
    <Stack sx={{ px: 6 }}>
      <Stack sx={{ pt: 12, pb: 8, gap: 2 }}>
        <Breadcrumbs maxItems={2} separator={<NavigateNext fontSize="small" />}>
          <Link
            href={`/${userType}/report/${uuid}`}
            color="text.secondary"
            style={{ textDecoration: "none" }}
          >
            <Typography variant="body2">レポートTOP</Typography>
          </Link>
          <Typography variant="body2">睡眠の長さ</Typography>
        </Breadcrumbs>
        <Typography
          variant="h5"
          sx={{ textAlign: "justify", color: "primary.main" }}
        >
          睡眠の長さ
        </Typography>
      </Stack>
      <Divider />
      <Stack sx={{ pt: "49px", pb: 6 }}>
        <SubtitleTypography>日別の測定結果と判定</SubtitleTypography>
      </Stack>
      <Stack sx={{ pb: 12, gap: 4 }}>
        <Stack gap={4} sx={{ mb: 4 }}>
          <Stack sx={{ textAlign: "right" }}>
            <Typography
              variant="caption"
              sx={{ "&:before": { content: "'■'", color: "#00A99D", pr: 1 } }}
            >
              推奨範囲
            </Typography>
          </Stack>
          <Chart
            standard_value={
              data.judge_and_evaluated_items.evaluated_items
                .average_time_of_sleep.standard_value
            }
            data={data.measurement_results}
          />
        </Stack>
        <QuantityTable data={data} />
        <Stack gap={4} sx={{ mb: 4 }}>
          <Typography variant="subtitle1" color="text.secondary">
            日別判定の基準
          </Typography>
          <Stack gap={2}>
            <TableContainer
              sx={{
                border: "solid 1px #D8D7D6",
                borderRadius: 0,
                "& thead tr th": {
                  p: "8px 0",
                  "&:nth-of-type(2)": {
                    borderLeft: "solid 1px #D8D7D6",
                  },
                },
                "& tbody tr td": {
                  p: 2,
                  "&:nth-of-type(2)": {
                    borderLeft: "solid 1px #D8D7D6",
                  },
                },
                "& tbody tr:last-of-type .MuiTableCell-root": {
                  borderBottom: "none",
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="caption" sx={{ pl: 2 }}>
                        睡眠時間
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="caption">判定</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography fontWeight={400} fontSize="13px">
                        {data.judge_and_evaluated_items.evaluated_items
                          .average_time_of_sleep.standard_value.from / 60}
                        〜
                        {data.judge_and_evaluated_items.evaluated_items
                          .average_time_of_sleep.standard_value.to / 60}
                        時間
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ReportDailyResultImg
                        type={3}
                        style={{ margin: "auto", display: "block" }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography fontWeight={400} fontSize="13px">
                        {data.judge_and_evaluated_items.evaluated_items
                          .too_short_sleep.appropriate_from / 60}
                        〜
                        {data.judge_and_evaluated_items.evaluated_items
                          .average_time_of_sleep.standard_value.from / 60}
                        時間、
                        {data.judge_and_evaluated_items.evaluated_items
                          .average_time_of_sleep.standard_value.to / 60}
                        〜
                        {data.judge_and_evaluated_items.evaluated_items
                          .appropriate_to / 60}
                        時間
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ReportDailyResultImg
                        type={2}
                        style={{ margin: "auto", display: "block" }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography fontWeight={400} fontSize="13px">
                        {data.judge_and_evaluated_items.evaluated_items
                          .too_short_sleep.appropriate_from / 60}
                        時間未満、
                        {data.judge_and_evaluated_items.evaluated_items
                          .appropriate_to / 60}
                        時間超
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ReportDailyResultImg
                        type={1}
                        style={{ margin: "auto", display: "block" }}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="caption"
              sx={{ textAlign: "right", color: "text.secondary" }}
            >
              ※対象者年齢：
              {
                data.judge_and_evaluated_items.evaluated_items
                  .average_time_of_sleep.age
              }
              の場合
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack sx={{ pb: 6 }}>
        <SubtitleTypography>睡眠の長さ判定</SubtitleTypography>
      </Stack>
      <Stack sx={{ pb: 12 }}>
        <RiskLabel
          type={SLEEP_JUDGE_TITLE_CONFIG[data.result]}
          title={data.result}
          summary={data.summary}
        />
      </Stack>
      <Divider />
      <Stack>
        <Typography variant="h6" sx={{ color: "text.secondary", pt: 8 }}>
          あわせて読みたい
        </Typography>

        <ReportNavigate
          navigates={[
            {
              link: `/${userType}/report/${uuid}/rhythm`,
              text: "睡眠のリズム",
            },
            {
              link: `/${userType}/report/${uuid}/quality`,
              text: "睡眠の質",
            },
          ]}
          reportUid={uuid}
        />
      </Stack>
    </Stack>
  );
}

function QuantityTable({ data }: { data: Quantity2Response }) {
  return (
    <ScrollableContainer>
      <TableContainer
        sx={{
          minWidth: "768px",
          borderRadius: 0,
          border: "solid 1px #D8D7D6",
          mb: 4,
          ml: 6,
          mr: 6,
          "& thead tr th, tr tbody td": {
            whiteSpace: "nowrap",
          },
          "& tbody tr td": {
            py: 4,
            px: 0,
            border: "none",
            "&:nth-of-type(1)": {
              px: 4,
              borderRight: "solid 1px #D8D7D6",
            },
            "&:not(:nth-of-type(-n + 1))": {
              textAlign: "center",
            },
          },
          "& tbody tr:nth-of-type(1) td": {
            borderBottom: "3px double #D8D7D6",
          },
          "& thead tr th": {
            p: "8px 0",
            "&:not(:nth-of-type(1))": {
              width: "83.71px",
              "& .MuiTypography-caption": {
                display: "block",
                textAlign: "center",
              },
            },
            "&:nth-of-type(1)": {
              width: "120px",
              borderRight: "solid 1px #D8D7D6",
              px: "16px",
              "& .MuiTypography-caption": {
                display: "block",
                textAlign: "left",
              },
            },
            "&:not(:nth-of-type(-n + 2))": {
              "& .MuiTypography-caption": {
                borderLeft: "solid 1px #D8D7D6",
              },
            },
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="caption">項目</Typography>
              </TableCell>
              {Array.from({ length: 7 }).map((_, i) => (
                <TableCell key={i}>
                  <Typography variant="caption">{i + 1}日目</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography fontWeight={400} fontSize="13px">
                  睡眠時間
                </Typography>
              </TableCell>
              {data.measurement_results.map((x, i) => (
                <TableCell key={i} sx={{ textAlign: "center" }}>
                  {x?.value != null ? (
                    <Typography fontWeight={400} fontSize="13px">
                      {formatDisplayTime(x.value)}
                    </Typography>
                  ) : (
                    <Typography fontWeight={400} fontSize="13px">
                      -
                    </Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography fontWeight={400} fontSize="13px">
                  日別判定
                </Typography>
              </TableCell>
              {data.each_day_sleep.map((x, i) => (
                <TableCell key={i} sx={{ textAlign: "center" }}>
                  {x != null ? (
                    <ReportDailyResultImg
                      type={x}
                      style={{ margin: "auto", display: "block" }}
                    />
                  ) : (
                    <Typography fontWeight={400} fontSize="13px">
                      -
                    </Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </ScrollableContainer>
  );
}

function Chart(props: {
  standard_value: {
    from: number;
    to: number;
  };
  data: {
    // 測定日
    date: string;
    // 睡眠の長さ
    value: number | null;
    // 注意フラグ
    is_warning: boolean | null;
  }[];
}) {
  const theme = useTheme();
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const options: Highcharts.Options = {
    chart: {
      type: "column",
      height: 300,
      marginLeft: 36,
      marginRight: 0,
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      labels: {
        rotation: 0,
        style: {
          color: theme.palette.text.secondary,
          fontSize: "10px",
        },
      },
      categories: props.data.map((d) => {
        const date = parse(d.date, "yyyy-MM-dd", new Date());
        return `${format(date, "M/d")}<br>${"日月火水木金土"[date.getDay()]}`;
      }),
      opposite: true,
      lineWidth: 0,
    },
    tooltip: {
      enabled: false,
    },
    yAxis: {
      title: {
        text: "",
      },
      labels: {
        enabled: false,
      },
      max: Math.max(...props.data.map((d) => d.value ?? 0)) + 4 * 60,
      min: 0,
      plotBands: [
        {
          color: "#e0f5f3",
          from: props.standard_value.from,
          to: props.standard_value.to,
        },
      ],
      plotLines: [
        {
          value: 0,
          color: "#e0e0e0",
        },
        {
          value: props.standard_value.from,
          width: 1,
          color: "#00A99D",
          label: {
            x: -35,
            y: 5,
            text: formatDisplayTime(props.standard_value.from),
            style: {
              color: "#00A99D",
              fontFamily: "Noto Sans JP",
              fontSize: "10px",
            },
          },
        },
        {
          value: props.standard_value.to,
          width: 1,
          color: "#00A99D",
          label: {
            x: -35,
            y: 5,
            text: formatDisplayTime(props.standard_value.to),
            style: {
              color: "#00A99D",
              fontFamily: "Noto Sans JP",
              fontSize: "10px",
            },
          },
        },
      ],
      gridLineWidth: 0,
    },
    legend: {
      align: "right",
      verticalAlign: "top",
      symbolHeight: 10,
      symbolRadius: 0,
      itemStyle: {
        color: theme.palette.text.secondary,
      },
    },
    plotOptions: {
      column: {
        borderRadius: 0,
      },
      series: {
        borderWidth: 0,
      },
    },
    series: [
      {
        type: "column",
        color: "#003C70",
        showInLegend: false,
        data: props.data.map((d) => ({ y: d.value, isWarning: d.is_warning })),
        dataLabels: {
          enabled: false,
        },
        events: {
          legendItemClick(e: SeriesLegendItemClickEventObject) {
            e.preventDefault();
          },
        },
      },
    ],
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      ref={chartComponentRef}
    />
  );
}
